import React from 'react';
import PropTypes from 'prop-types'; // <-- Added
import { FormattedMessage, injectIntl } from 'react-intl';
import _ from 'lodash';
import { connect } from 'react-redux';
import { Radio, Input, Checkbox, Form, Button, InputNumber, Skeleton, Table, Dropdown, Menu, Icon, Modal } from 'antd';
import { getAllowedSmtpOptions, PROVIDER_TYPE } from '../../Utils/SmtpUtils';
import './EmailSettings.scss';
import styles from './EmailSettings.module.scss';
import DefaultSmtpComponent from './DefaultSmtpComponent';
import InfoIconWithTooltip from '../Common/InfoIconWithTooltip/InfoIconWithTooltip';
import emailSetting from './EmailSettingsMessage';
import placeholder from '../Placeholders/PlaceholdersMessages';
import { getFeatureToggleList } from '../../Reducers/FeatureToggleReducer.ts';

const initialState = {
  Name: '',
  OutgoingServerHost: '',
  OutgoingServerPort: null,
  Username: '',
  Password: '',
  EnableSsl: false,
  IsOrganizationDefaultProvider: false,
  activeRadio: PROVIDER_TYPE.manual,
  validateStatus: {
    Name: 'success',
    OutgoingServerHost: 'success',
    OutgoingServerPort: 'success',
    Username: 'success',
    Password: 'success',
  },
  emailSmtpConfigState: {},
  buttonToggleState: 'none',
  manualProviders: [],
  showForm: false,
  showEditModal: false,
  showViewModal: false,
  editingProviderId: null,
  inlineFormLoading: false,
  modalSaveLoading: false,
  settingsSaveLoading: false,
};

const mapStateToProps = state => {
  return {
    featureToggleList: getFeatureToggleList(state),
  };
};

class EmailSettings extends React.Component {
  constructor(props) {
    super(props);
    this.onSave = this.onSave.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSSLChange = this.handleSSLChange.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.clearErrorMsg = this.clearErrorMsg.bind(this);
    this.state = initialState;
    this.isInitialValuesSet = false;
  }

  async componentDidMount() {
    const { fetchUserEmails } = this.props;
    await fetchUserEmails();
  }

  componentDidUpdate(prevProps) {
    const { emailConfigurationApiStatus: prevEmailConfigurationApiStatus, emailProviders: prevEmailProviders } =
      prevProps;
    const { emailConfigurationApiStatus, emailProviders } = this.props;

    if (
      !this.isInitialValuesSet &&
      prevEmailConfigurationApiStatus === 'INPROGRESS' &&
      emailConfigurationApiStatus === 'COMPLETED'
    ) {
      this.setInitialState();
      this.isInitialValuesSet = true;
      return;
    }

    if (this.isInitialValuesSet && emailProviders !== prevEmailProviders) {
      const manualProviders = emailProviders?.filter(provider => provider.AuthScheme === PROVIDER_TYPE.manual) || [];
      this.setState({ manualProviders });
    }
  }

  setInitialState = () => {
    const { emailSmtpConfiguration, emailProviders } = this.props;
    const manualProviders = emailProviders?.filter(provider => provider.AuthScheme === PROVIDER_TYPE.manual) || [];
    const newState = {
      manualProviders,
      OutgoingServerHost: '',
      OutgoingServerPort: null,
      Username: '',
      Password: '',
      EnableSsl: false,
      providerId: '',
      isMultipleSenderAddressConfigurationAllowed: emailSmtpConfiguration.IsMultipleSenderAddressConfigurationAllowed,
      isThirdPartyEmailConfigurationAllowed: emailSmtpConfiguration.IsThirdPartyEmailConfigurationAllowed,
      activeRadio: PROVIDER_TYPE.manual,
      showForm: false,
      showViewModal: false,
      editingProviderId: null,
      isDefaultProvider: false,
    };
    this.setState({ ...newState });
  };

  validateForm() {
    const { Name, OutgoingServerHost, OutgoingServerPort } = this.state;
    const name = typeof Name === 'string' ? Name.trim() : null;
    const outServerHost = typeof OutgoingServerHost === 'string' ? OutgoingServerHost.trim() : null;
    const outServerPort = typeof OutgoingServerPort === 'number' ? OutgoingServerPort : null;

    if (!name && !outServerHost && outServerPort === null) {
      return { validated: true, cleared: true };
    }
    const validateStatus = {
      Name: name ? 'success' : 'error',
      OutgoingServerHost: outServerHost ? 'success' : 'error',
      OutgoingServerPort: outServerPort !== null ? 'success' : 'error',
    };
    this.setState({ validateStatus });
    if (!name || !outServerHost || outServerPort === null) {
      return { validated: false };
    }
    return { validated: true };
  }

  onSaveInlineForm = () => {
    const { updateEmailSmtpConfig, isAdmin, fetchEmailConfig, SetNotification } = this.props;
    const {
      Name,
      OutgoingServerHost,
      OutgoingServerPort,
      Username,
      Password,
      EnableSsl,
      IsOrganizationDefaultProvider,
    } = this.state;

    const { validated } = this.validateForm();
    if (!validated) {
      return;
    }

    this.setState({ inlineFormLoading: true });

    const emailUpdate = {
      Name: Name.trim(),
      Configuration: {
        EnableSsl: !!EnableSsl,
        Host: OutgoingServerHost.trim(),
        Port: OutgoingServerPort,
        Username: Username.trim(),
        Password: Password?.trim() || null,
        IsOrganizationDefaultProvider: !!IsOrganizationDefaultProvider,
      },
      AuthScheme: PROVIDER_TYPE.manual,
    };

    updateEmailSmtpConfig(emailUpdate, { isAdmin }, ({ updatedProviderId }) => {
      this.setState({
        Name: '',
        OutgoingServerHost: '',
        OutgoingServerPort: null,
        Username: '',
        Password: '',
        EnableSsl: false,
        IsOrganizationDefaultProvider: false,
        inlineFormLoading: false,
      });
      fetchEmailConfig({ isAdmin });
      SetNotification('SUCCESS', {
        messageId: 'savedSuccessfully',
      });
    }).catch(() => {
      this.setState({ inlineFormLoading: false });
      SetNotification('ERROR', {
        messageId: 'oopsSomethingJustWentWrong',
      });
    });
  };

  onSave = () => {
    const { updateEmailSmtpConfig, isAdmin, updateEmailSmtpSettings, fetchEmailConfig, SetNotification } = this.props;
    const {
      Name,
      OutgoingServerHost,
      OutgoingServerPort,
      Username,
      Password,
      EnableSsl,
      IsOrganizationDefaultProvider,
      editingProviderId,
      isThirdPartyEmailConfigurationAllowed,
      isMultipleSenderAddressConfigurationAllowed,
      showEditModal,
    } = this.state;

    if (showEditModal) {
      const { validated } = this.validateForm();
      if (!validated) {
        return;
      }
      this.setState({ modalSaveLoading: true });
      const emailUpdate = {
        Name: Name.trim(),
        Configuration: {
          EnableSsl: !!EnableSsl,
          Host: OutgoingServerHost.trim(),
          Port: OutgoingServerPort,
          Username: Username.trim(),
          Password: Password?.trim() || null,
          IsOrganizationDefaultProvider: !!IsOrganizationDefaultProvider,
        },
        AuthScheme: PROVIDER_TYPE.manual,
      };

      if (editingProviderId) {
        emailUpdate.Id = editingProviderId;
      }

      updateEmailSmtpConfig(emailUpdate, { isAdmin, providerId: editingProviderId }, ({ updatedProviderId }) => {
        this.setState({
          showEditModal: false,
          editingProviderId: null,
          Name: '',
          OutgoingServerHost: '',
          OutgoingServerPort: null,
          Username: '',
          Password: '',
          EnableSsl: false,
          IsOrganizationDefaultProvider: false,
          modalSaveLoading: false,
        });
        fetchEmailConfig({ isAdmin });
        SetNotification('SUCCESS', {
          messageId: 'savedSuccessfully',
        });
      }).catch(() => {
        this.setState({ modalSaveLoading: false });
        SetNotification('ERROR', {
          messageId: 'oopsSomethingJustWentWrong',
        });
      });
      return;
    }

    if (this.isEmailSmtpConfigurationChanged()) {
      this.setState({ settingsSaveLoading: true });
      updateEmailSmtpSettings({
        IsMultipleSenderAddressConfigurationAllowed: isMultipleSenderAddressConfigurationAllowed,
        IsThirdPartyEmailConfigurationAllowed: isThirdPartyEmailConfigurationAllowed,
      })
        .then(() => {
          this.setState({ settingsSaveLoading: false });
          SetNotification('SUCCESS', {
            messageId: 'savedSuccessfully',
          });
        })
        .catch(() => {
          this.setState({ settingsSaveLoading: false });
          SetNotification('ERROR', {
            messageId: 'oopsSomethingJustWentWrong',
          });
        });
    }
  };

  clearErrorMsg(field) {
    const { validateStatus } = this.state;
    const newValidateStatus = { ...validateStatus };
    newValidateStatus[field] = 'success';
    this.setState({
      validateStatus: newValidateStatus,
    });
  }

  isEmailSmtpConfigurationChanged() {
    const { emailSmtpConfiguration } = this.props;
    const { isMultipleSenderAddressConfigurationAllowed, isThirdPartyEmailConfigurationAllowed } = this.state;

    return (
      isMultipleSenderAddressConfigurationAllowed !==
      emailSmtpConfiguration.IsMultipleSenderAddressConfigurationAllowed ||
      isThirdPartyEmailConfigurationAllowed !== emailSmtpConfiguration.IsThirdPartyEmailConfigurationAllowed
    );
  }

  isEmailConfigurationChanged() {
    const { emailProviders } = this.props;
    const {
      editingProviderId,
      Name,
      OutgoingServerHost,
      OutgoingServerPort,
      Username,
      Password,
      EnableSsl,
      IsOrganizationDefaultProvider,
    } = this.state;

    if (!editingProviderId) {
      return OutgoingServerHost?.trim() && OutgoingServerPort;
    }

    const manualProviderConfig = emailProviders?.find(p => p.Id === editingProviderId);
    if (!manualProviderConfig) {
      return true;
    }

    const { Configuration = {} } = manualProviderConfig;
    return !(
      _.get(manualProviderConfig, 'Name', '') === Name?.trim() &&
      _.get(Configuration, 'Host', '') === OutgoingServerHost?.trim() &&
      _.get(Configuration, 'Port', null) === OutgoingServerPort &&
      _.get(Configuration, 'Username', '') === Username?.trim() &&
      _.get(Configuration, 'EnableSsl', false) === EnableSsl &&
      _.get(Configuration, 'IsOrganizationDefaultProvider', false) === IsOrganizationDefaultProvider &&
      (Password === Configuration.Password || !Password)
    );
  }

  handleChange(key, e) {
    if (key === 'OutgoingServerPort') {
      this.setState({
        [key]: e,
      });
      this.clearErrorMsg('OutgoingServerHost');
      return;
    }
    this.setState({
      [key]: e.target.value,
    });
    this.clearErrorMsg('OutgoingServerPort');
  }

  handleSSLChange(e) {
    this.setState({
      EnableSsl: e.target.checked,
    });
  }

  handleOrganizationDefaultChange = e => {
    this.setState({
      IsOrganizationDefaultProvider: e.target.checked,
    });
  };

  handleRadioChange = e => {
    this.setState({
      activeRadio: e.target.value,
    });
  };

  handleAddSmtp = () => {
    this.setState({
      showEditModal: true,
      editingProviderId: null,
      Name: '',
      OutgoingServerHost: '',
      OutgoingServerPort: null,
      Username: '',
      Password: '',
      EnableSsl: false,
      IsOrganizationDefaultProvider: false,
    });
  };

  setProviderFormState = (provider, modalType) => {
    const { Configuration = {}, Id, Name } = provider;
    this.setState({
      [modalType]: true,
      editingProviderId: Id,
      Name: Name ?? '',
      OutgoingServerHost: Configuration.Host ?? '',
      OutgoingServerPort: Configuration.Port ?? null,
      Username: Configuration.Username ?? '',
      Password: Configuration.Password ?? '',
      EnableSsl: !!Configuration.EnableSsl,
      IsOrganizationDefaultProvider: !!Configuration.IsOrganizationDefaultProvider,
    });
  };

  handleEditSmtp = provider => {
    this.setProviderFormState(provider, 'showEditModal');
  };

  handleViewSmtp = provider => {
    this.setProviderFormState(provider, 'showViewModal');
  };

  handleDeleteSmtp = providerId => {
    const { deleteEmailConfig, isAdmin, fetchEmailConfig, SetNotification } = this.props;
    deleteEmailConfig(providerId, { isAdmin }, () => {
      fetchEmailConfig({ isAdmin });
      SetNotification('SUCCESS', {
        messageId: 'deletedSuccessfully',
      });
    }).catch(() => {
      SetNotification('ERROR', {
        messageId: 'oopsSomethingJustWentWrong',
      });
    });
  };

  handleCancelForm = () => {
    this.setState({
      showEditModal: false,
      showViewModal: false,
      editingProviderId: null,
      Name: '',
      OutgoingServerHost: '',
      OutgoingServerPort: null,
      Username: '',
      Password: '',
      EnableSsl: false,
      IsOrganizationDefaultProvider: false,
    });
  };

  getEmailSmtpConfigurationButtons = () => {
    const { activeRadio } = this.state;
    const allowedSmtpOptions = getAllowedSmtpOptions({
      manualValue: PROVIDER_TYPE.manual,
    });

    return (
      <div className="email-settings-row">
        <Radio.Group value={activeRadio} onChange={this.handleRadioChange}>
          {allowedSmtpOptions.map(smtpOption => (
            <Radio
              value={smtpOption.value}
              key={smtpOption.value}
              style={{ color: 'rgba(7, 16, 26, 0.9)', fontWeight: 500 }}
            >
              {smtpOption.label}
            </Radio>
          ))}
        </Radio.Group>
      </div>
    );
  };

  onMultipleSenderAddressConfigurationChange = e => {
    this.setState({
      isMultipleSenderAddressConfigurationAllowed: e.target.checked,
    });
  };

  onThirdPartyConfigurationChange = e => {
    this.setState({
      isThirdPartyEmailConfigurationAllowed: e.target.checked,
    });
  };

  getIsSaveButtonDisabled = () => {
    const { showEditModal } = this.state;
    if (showEditModal) {
      return !this.isEmailConfigurationChanged();
    }
    return !this.isEmailSmtpConfigurationChanged() && !this.isEmailConfigurationChanged();
  };

  isSslDisabled = () => {
    const { OutgoingServerHost, OutgoingServerPort } = this.state;
    return !OutgoingServerHost || !OutgoingServerPort;
  };

  renderSmtpFormFields = () => {
    const {
      Name,
      OutgoingServerHost,
      OutgoingServerPort,
      Username,
      Password,
      validateStatus,
    } = this.state;
    const { intl } = this.props;

    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: 16 }}>
          <Form.Item
            label={<FormattedMessage {...emailSetting.nameLabel} />}
            validateStatus={validateStatus.Name}
            help={
              validateStatus.Name === 'error' ? <FormattedMessage {...emailSetting.enterNameLabel} /> : null
            }
            colon={false}
            required
          >
            <Input
              id="name-input"
              value={Name}
              onChange={e => this.handleChange('Name', e)}
              onFocus={() => this.clearErrorMsg('Name')}
              placeholder={intl.formatMessage({ ...placeholder.NameLabel })}
            />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage {...emailSetting.sMTPServerLabel} />}
            validateStatus={validateStatus.OutgoingServerHost}
            help={
              validateStatus.OutgoingServerHost === 'error' ? (
                <FormattedMessage {...emailSetting.enterSmtpServerLabel} />
              ) : null
            }
            colon={false}
            required
          >
            <Input
              id="smtp-server-input"
              value={OutgoingServerHost}
              onChange={e => this.handleChange('OutgoingServerHost', e)}
              onFocus={() => this.clearErrorMsg('OutgoingServerHost')}
              placeholder={intl.formatMessage({ ...placeholder.SmtpServerLabel })}
            />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage {...emailSetting.sMTPPortLabel} />}
            validateStatus={validateStatus.OutgoingServerPort}
            help={
              validateStatus.OutgoingServerPort === 'error' ? (
                <FormattedMessage {...emailSetting.enterSmtpPortLabel} />
              ) : null
            }
            colon={false}
            required
          >
            <InputNumber
              style={{ width: '100%' }}
              value={OutgoingServerPort}
              onChange={e => this.handleChange('OutgoingServerPort', e)}
              onFocus={() => this.clearErrorMsg('OutgoingServerPort')}
              placeholder={intl.formatMessage({ ...placeholder.smtpPortLabel })}
            />
          </Form.Item>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: 16 }}>
          <Form.Item label={<FormattedMessage {...emailSetting.userNameLabel} />} colon={false}>
            <Input
              id="username-input"
              value={Username}
              autoComplete="false"
              onChange={e => this.handleChange('Username', e)}
              onFocus={() => this.clearErrorMsg('Username')}
              placeholder={intl.formatMessage({ ...placeholder.userNameLabel })}
            />
            <span className={styles.formInputHelperText}>
              <FormattedMessage {...emailSetting.optionalIfSMTPServerLabel} />
            </span>
          </Form.Item>
          <Form.Item label={<FormattedMessage {...emailSetting.passwordLabel} />} colon={false}>
            <Input
              style={{ width: '100%' }}
              id="password-input"
              value={Password}
              type="password"
              autoComplete="new-password"
              onChange={e => this.handleChange('Password', e)}
              onFocus={() => this.clearErrorMsg('Password')}
              placeholder={intl.formatMessage({ ...placeholder.passwordLabel })}
            />
            <span className={styles.formInputHelperText}>
              <FormattedMessage {...emailSetting.optionalIfSMTPServerLabel} />
            </span>
          </Form.Item>
        </div>
      </>
    );
  };

  renderInlineSmtpForm = () => {
    const {
      EnableSsl,
      IsOrganizationDefaultProvider,
    } = this.state;

    return (
      <div className={styles.inlineSmtpForm} style={{ marginTop: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
        <div className={styles.infoIconWithContent} style={{ marginBottom: 16 }}>
          <span style={{ fontSize: '16px', fontWeight: 500 }}>
            <FormattedMessage {...emailSetting.customEmailConfigurationLabel} />
          </span>
          <InfoIconWithTooltip tooltipTitle={<FormattedMessage {...emailSetting.tooltipForCustomMailLabel} />} />
        </div>

        {this.renderSmtpFormFields()}

        <div style={{ marginBottom: 16, display: 'flex', gap: '24px' }}>
          <Checkbox checked={EnableSsl} onChange={this.handleSSLChange} disabled={this.isSslDisabled()}>
            <FormattedMessage {...emailSetting.enableSSlLable} />
          </Checkbox>
          <Checkbox
            checked={IsOrganizationDefaultProvider}
            onChange={this.handleOrganizationDefaultChange}
          >
            Set as organization default
          </Checkbox>
        </div>

        <div style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            onClick={this.onSaveInlineForm}
            loading={this.state.inlineFormLoading}
            disabled={!this.isEmailConfigurationChanged()}
            style={{ borderRadius: '15px' }}
          >
            <FormattedMessage {...emailSetting.saveChangesLabel} />
          </Button>
        </div>
      </div>
    );
  };

  renderSmtpTable = () => {
    const { manualProviders } = this.state;

    const columns = [
      {
        title: 'NAME',
        dataIndex: 'Name',
        key: 'name',
        render: (text, record) => <div>{text}</div>,
      },
      {
        title: 'SMTP SERVER',
        dataIndex: ['Configuration', 'Host'],
        key: 'host',
        render: (text, record) => (
          <div>
            {text}
            {record.Configuration?.IsOrganizationDefaultProvider && (
              <span style={{ marginLeft: 8, color: '#666' }}>(Org Default)</span>
            )}
          </div>
        ),
      },
      {
        title: 'SMTP PORT',
        dataIndex: ['Configuration', 'Port'],
        key: 'port',
      },
      {
        title: 'SSL STATE',
        dataIndex: ['Configuration', 'EnableSsl'],
        key: 'ssl',
        render: ssl => (ssl ? 'Enabled' : 'Disabled'),
      },
      {
        title: 'ACTION',
        key: 'action',
        render: (text, record) => {
          const menu = (
            <Menu>
              <Menu.Item key="view" onClick={() => this.handleViewSmtp(record)}>
                View
              </Menu.Item>
              <Menu.Item key="edit" onClick={() => this.handleEditSmtp(record)}>
                Edit
              </Menu.Item>
              <Menu.Item key="delete" onClick={() => this.handleDeleteSmtp(record.Id)}>
                Delete
              </Menu.Item>
            </Menu>
          );

          return (
            <Dropdown overlay={menu} trigger={['click']}>
              <Button
                type="button"
                style={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                }}
              >
                <Icon type="ellipsis" style={{ fontSize: 18 }} />
              </Button>
            </Dropdown>
          );
        },
      },
    ];

    return (
      <div className={styles.smtpTableContainer}>
        <Table
          columns={columns}
          dataSource={manualProviders}
          rowKey="Id"
          pagination={false}
          className={styles.smtpTable}
          footer={() => (
            <div style={{ textAlign: 'left' }}>
              <Button
                type="link"
                onClick={this.handleAddSmtp}
                style={{
                  padding: 0,
                  fontSize: '14px',
                  textDecoration: 'underline',
                }}
              >
                + Add SMTP Server
              </Button>
            </div>
          )}
        />
      </div>
    );
  };

  render() {
    const {
      version,
      userEmails,
      currentUser,
      orgDefaultEmail,
      intl,
      featureToggleList,
    } = this.props;
    const {
      Name,
      OutgoingServerHost,
      OutgoingServerPort,
      Username,
      Password,
      EnableSsl,
      IsOrganizationDefaultProvider,
      validateStatus,
      activeRadio,
      showLoadingScreen,
      isThirdPartyEmailConfigurationAllowed,
      isMultipleSenderAddressConfigurationAllowed,
      manualProviders,
      showEditModal,
      showViewModal,
    } = this.state;
    const title = version === 'ats' ? 'Admin ' : '';
    const defaultAryaEmailProvider = featureToggleList.DefaultAryaEmailProvider.IsEnabled;
    const emailConfigurationTitle = <FormattedMessage {...emailSetting.EmailConfigurationLabel} values={{ title }} />;
    return (
      <div>
        {defaultAryaEmailProvider && (
          <div className="email-configuration-header">
            <div className="email-configuration-title">{emailConfigurationTitle}</div>
            <div className={styles.defaultEmailConfig}>
              <div className={styles.infoIconWithContent}>
                <div className={styles.defaultEmailTitle}>
                  <FormattedMessage {...emailSetting.defaultEmailConfigurationLabel} />
                  &nbsp;
                </div>
                <InfoIconWithTooltip tooltipTitle={<FormattedMessage {...emailSetting.infoIconTooltipLabel} />} />
              </div>
              <DefaultSmtpComponent
                userEmails={userEmails}
                orgDefaultEmail={orgDefaultEmail}
                currentUser={currentUser}
              />
            </div>
          </div>
        )}
        <div className="email-settings-container">
          <div className="email-settings-admin">
            <div className={styles.infoIconWithContent}>
              <div className={styles.defaultEmailTitle}>
                <FormattedMessage {...emailSetting.customEmailConfigurationLabel} />
                &nbsp;
              </div>
            </div>
          </div>
          <Skeleton loading={!this.isInitialValuesSet || showLoadingScreen} active paragraph={{ rows: 5 }}>
            {this.getEmailSmtpConfigurationButtons()}
            {activeRadio === PROVIDER_TYPE.manual ? (
              <div>
                {manualProviders.length === 0 ? this.renderInlineSmtpForm() : this.renderSmtpTable()}
                <Modal
                  title={
                    <div className={styles.infoIconWithContent}>
                      <span style={{ marginRight: '4px' }}>
                        <FormattedMessage {...emailSetting.customEmailConfigurationLabel} />
                      </span>
                      <InfoIconWithTooltip tooltipTitle={<FormattedMessage {...emailSetting.tooltipForCustomMailLabel} />} />
                    </div>
                  }
                  visible={showEditModal}
                  onCancel={this.handleCancelForm}
                  footer={[
                    <Button key="cancel" onClick={this.handleCancelForm} style={{ borderRadius: '15px' }}>
                      Cancel
                    </Button>,
                    <Button
                      key="submit"
                      type="primary"
                      onClick={this.onSave}
                      loading={this.state.modalSaveLoading}
                      disabled={this.getIsSaveButtonDisabled()}
                      style={{ borderRadius: '15px' }}
                    >
                      <FormattedMessage {...emailSetting.saveChangesLabel} />
                    </Button>,
                  ]}
                  width={800}
                  style={{ width: '120vw' }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <div>
                    {this.renderSmtpFormFields()}
                    <Checkbox checked={EnableSsl} onChange={this.handleSSLChange} disabled={this.isSslDisabled()}>
                      <FormattedMessage {...emailSetting.enableSSlLable} />
                    </Checkbox>
                    <Checkbox
                      checked={IsOrganizationDefaultProvider}
                      onChange={this.handleOrganizationDefaultChange}
                      style={{ marginTop: 16 }}
                    >
                      Set as organization default
                    </Checkbox>
                  </div>
                </Modal>
                <Modal
                  title="View SMTP Configuration"
                  visible={showViewModal}
                  onCancel={this.handleCancelForm}
                  footer={[
                    <Button key="close" onClick={this.handleCancelForm} style={{ borderRadius: '15px' }}>
                      Close
                    </Button>,
                  ]}
                  width={800}
                  style={{ width: '120vw' }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                      <Form.Item label={<FormattedMessage {...emailSetting.nameLabel} />} colon={false}>
                        <Input value={Name} disabled />
                      </Form.Item>
                      <Form.Item label={<FormattedMessage {...emailSetting.sMTPServerLabel} />} colon={false}>
                        <Input value={OutgoingServerHost} disabled />
                      </Form.Item>
                      <Form.Item label={<FormattedMessage {...emailSetting.sMTPPortLabel} />} colon={false}>
                        <InputNumber style={{ width: '100%' }} value={OutgoingServerPort} disabled />
                      </Form.Item>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <Form.Item label={<FormattedMessage {...emailSetting.userNameLabel} />} colon={false}>
                        <Input value={Username} disabled />
                      </Form.Item>
                      <Form.Item label={<FormattedMessage {...emailSetting.passwordLabel} />} colon={false}>
                        <Input value="••••••••" type="password" disabled />
                      </Form.Item>
                    </div>
                    <Checkbox checked={EnableSsl} disabled>
                      <FormattedMessage {...emailSetting.enableSSlLable} />
                    </Checkbox>
                  </div>
                </Modal>
                <div className={styles.smtpSettings}>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}
                  >
                    <div className={styles.additionalSettings}>
                      <FormattedMessage {...emailSetting.additionalSettingsLabel} />
                    </div>
                    <Button
                      className="app-save"
                      onClick={this.onSave}
                      loading={this.state.settingsSaveLoading}
                      disabled={!this.isEmailSmtpConfigurationChanged()}
                    >
                      <FormattedMessage {...emailSetting.saveChangesLabel} />
                    </Button>
                  </div>
                  <Checkbox
                    checked={isThirdPartyEmailConfigurationAllowed}
                    onChange={this.onThirdPartyConfigurationChange}
                  >
                    <FormattedMessage {...emailSetting.enableThirdpartyEmailConfigurationLabel} />
                  </Checkbox>
                  <Checkbox
                    checked={isMultipleSenderAddressConfigurationAllowed}
                    onChange={this.onMultipleSenderAddressConfigurationChange}
                  >
                    <FormattedMessage {...emailSetting.enableEndUsersToConfigureLable} />
                  </Checkbox>
                </div>
              </div>
            ) : null}
          </Skeleton>
        </div>
      </div>
    );
  }
}

EmailSettings.propTypes = {
  version: PropTypes.string,
  userEmails: PropTypes.arrayOf(
    PropTypes.shape({
      EmailId: PropTypes.string,
      DisplayName: PropTypes.string,
    })
  ),
  currentUser: PropTypes.shape({
    UserId: PropTypes.string,
    UserName: PropTypes.string,
    Email: PropTypes.string,
  }),
  orgDefaultEmail: PropTypes.shape({
    EmailId: PropTypes.string,
    DisplayName: PropTypes.string,
  }),
  emailSettingsApiStatus: PropTypes.string,
  emailConfigurationApiStatus: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
    locale: PropTypes.string,
  }).isRequired,
  featureToggleList: PropTypes.shape({
    DefaultAryaEmailProvider: PropTypes.shape({
      IsEnabled: PropTypes.bool,
    }),
  }).isRequired,

  fetchUserEmails: PropTypes.func,
  updateEmailSmtpConfig: PropTypes.func,
  updateEmailSmtpSettings: PropTypes.func,
  fetchEmailConfig: PropTypes.func,
  deleteEmailConfig: PropTypes.func,
  SetNotification: PropTypes.func,

  isAdmin: PropTypes.bool,
  emailProviders: PropTypes.arrayOf(
    PropTypes.shape({
      ProviderId: PropTypes.string,
      ProviderName: PropTypes.string,
    })
  ),
  emailSmtpConfiguration: PropTypes.shape({
    IsMultipleSenderAddressConfigurationAllowed: PropTypes.bool,
    IsThirdPartyEmailConfigurationAllowed: PropTypes.bool,
  }),
};

EmailSettings.defaultProps = {
  version: null,
  userEmails: [],
  currentUser: null,
  orgDefaultEmail: null,
  emailSettingsApiStatus: null,
  emailConfigurationApiStatus: null,
  fetchUserEmails: () => { },
  updateEmailSmtpConfig: () => { },
  updateEmailSmtpSettings: () => { },
  fetchEmailConfig: () => { },
  deleteEmailConfig: () => { },
  SetNotification: () => { },
  isAdmin: false,
  emailProviders: [],
  emailSmtpConfiguration: null,
};

export default connect(mapStateToProps, null)(injectIntl(EmailSettings));
